import { Icon } from '@iconify/react/dist/iconify.js'
import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../services/api'

const FeedbackManagement = () => {
    const [feedbackItems, setFeedbackItems] = useState([]);
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [feedbackStats, setFeedbackStats] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all');
    const [selectedItems, setSelectedItems] = useState([]);
    const [showResponseModal, setShowResponseModal] = useState(false);
    const [adminResponse, setAdminResponse] = useState('');
    const [responseStatus, setResponseStatus] = useState('REVIEWED');
    const [isPublic, setIsPublic] = useState(false);

    // Fetch all feedback
    const fetchFeedback = async (filter = {}) => {
        try {
            setLoading(true);
            const response = await api.feedback.getAll(filter);
            if (response.success) {
                setFeedbackItems(response.data);
            } else {
                setError(response.message || 'Failed to fetch feedback');
            }
        } catch (err) {
            setError('Error fetching feedback: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Fetch feedback statistics
    const fetchFeedbackStats = async () => {
        try {
            const response = await api.feedback.getFeedbackStats();
            if (response.success) {
                setFeedbackStats(response.data);
            }
        } catch (err) {
            console.error('Error fetching feedback stats:', err);
        }
    };

    // Handle filter change
    const handleFilterChange = (filter) => {
        setActiveFilter(filter);
        let filterParams = {};

        switch (filter) {
            case 'pending':
                filterParams = { status: 'PENDING' };
                break;
            case 'reviewed':
                filterParams = { status: 'REVIEWED' };
                break;
            case 'resolved':
                filterParams = { status: 'RESOLVED' };
                break;
            case 'rejected':
                filterParams = { status: 'REJECTED' };
                break;
            case 'bug-reports':
                filterParams = { type: 'BUG_REPORT' };
                break;
            case 'feature-requests':
                filterParams = { type: 'FEATURE_REQUEST' };
                break;
            case 'general':
                filterParams = { type: 'GENERAL' };
                break;
            case 'public':
                filterParams = { isPublic: true };
                break;
            default:
                filterParams = {};
                break;
        }

        fetchFeedback(filterParams);
    };

    // Handle feedback selection
    const handleSelectFeedback = (id) => {
        if (selectedItems.includes(id)) {
            setSelectedItems(selectedItems.filter(itemId => itemId !== id));
        } else {
            setSelectedItems([...selectedItems, id]);
        }
    };

    // Handle select all feedback
    const handleSelectAll = () => {
        if (selectedItems.length === feedbackItems.length) {
            setSelectedItems([]);
        } else {
            setSelectedItems(feedbackItems.map(item => item.id));
        }
    };

    // Handle view feedback details
    const handleViewFeedback = async (id) => {
        try {
            const response = await api.feedback.getById(id);
            if (response.success) {
                setSelectedFeedback(response.data);
                setAdminResponse(response.data.adminResponse || '');
                setResponseStatus(response.data.status);
                setIsPublic(response.data.isPublic);
                setShowResponseModal(true);
            } else {
                setError(response.message || 'Failed to fetch feedback details');
            }
        } catch (err) {
            setError('Error fetching feedback details: ' + err.message);
        }
    };

    // Handle submit admin response
    const handleSubmitResponse = async () => {
        try {
            if (!selectedFeedback) return;
            
            const response = await api.feedback.adminUpdate(selectedFeedback.id, {
                adminResponse,
                status: responseStatus,
                isPublic
            });
            
            if (response.success) {
                setShowResponseModal(false);
                fetchFeedback(); // Refresh feedback list
                setSelectedFeedback(null);
            } else {
                setError(response.message || 'Failed to update feedback');
            }
        } catch (err) {
            setError('Error updating feedback: ' + err.message);
        }
    };

    // Handle delete feedback
    const handleDeleteFeedback = async (id) => {
        try {
            const confirmed = window.confirm('Are you sure you want to delete this feedback?');
            if (!confirmed) return;
            
            const response = await api.feedback.delete(id);
            if (response.success) {
                setFeedbackItems(feedbackItems.filter(item => item.id !== id));
                setSelectedItems(selectedItems.filter(itemId => itemId !== id));
            } else {
                setError(response.message || 'Failed to delete feedback');
            }
        } catch (err) {
            setError('Error deleting feedback: ' + err.message);
        }
    };

    // Delete selected feedback items
    const handleDeleteSelected = async () => {
        const confirmed = window.confirm(`Are you sure you want to delete ${selectedItems.length} selected feedback items?`);
        if (!confirmed) return;
        
        try {
            const deletePromises = selectedItems.map(id => api.feedback.delete(id));
            await Promise.all(deletePromises);
            
            setFeedbackItems(feedbackItems.filter(item => !selectedItems.includes(item.id)));
            setSelectedItems([]);
        } catch (err) {
            setError('Error deleting selected feedback: ' + err.message);
        }
    };

    // Fetch feedback on component mount
    useEffect(() => {
        fetchFeedback();
        fetchFeedbackStats();
    }, []);

    // Helper function to get count for each status or type
    const getCountForType = (type) => {
        if (!feedbackStats) return 0;
        
        const typeData = feedbackStats.byType.find(item => item.type === type);
        return typeData ? typeData.count : 0;
    };
    
    const getCountForStatus = (status) => {
        if (!feedbackStats) return 0;
        
        const statusData = feedbackStats.byStatus.find(item => item.status === status);
        return statusData ? statusData.count : 0;
    };

    return (
        <div className="row gy-4">
            <div className="col-xxl-3">
                <div className="card h-100 p-0">
                    <div className="card-body p-24">
                        <h4 className="text-xl fw-semibold text-primary-light mb-16">
                            Feedback Management
                        </h4>
                        <div className="mt-16">
                            <h6 className="text-lg fw-semibold text-primary-light mb-16">
                                FILTERS
                            </h6>
                            <ul>
                                <li className={`mb-4 ${activeFilter === 'all' ? 'item-active' : ''}`}>
                                    <button
                                        onClick={() => handleFilterChange('all')}
                                        className="bg-hover-primary-50 px-12 py-8 w-100 radius-8 text-secondary-light border-0"
                                    >
                                        <span className="d-flex align-items-center gap-10 justify-content-between w-100">
                                            <span className="d-flex align-items-center gap-10">
                                                <span className="icon text-xxl line-height-1 d-flex">
                                                    <Icon
                                                        icon="uil:envelope"
                                                        className="icon line-height-1"
                                                    />
                                                </span>
                                                <span className="fw-semibold">All Feedback</span>
                                            </span>
                                            <span className="fw-medium">{feedbackStats?.totalCount || 0}</span>
                                        </span>
                                    </button>
                                </li>
                                <li className={`mb-4 ${activeFilter === 'pending' ? 'item-active' : ''}`}>
                                    <button
                                        onClick={() => handleFilterChange('pending')}
                                        className="bg-hover-primary-50 px-12 py-8 w-100 radius-8 text-secondary-light border-0"
                                    >
                                        <span className="d-flex align-items-center gap-10 justify-content-between w-100">
                                            <span className="d-flex align-items-center gap-10">
                                                <span className="icon text-xxl line-height-1 d-flex">
                                                    <Icon
                                                        icon="mdi:clock-outline"
                                                        className="icon line-height-1"
                                                    />
                                                </span>
                                                <span className="fw-semibold">Pending</span>
                                            </span>
                                            <span className="fw-medium">{getCountForStatus('PENDING')}</span>
                                        </span>
                                    </button>
                                </li>
                                <li className={`mb-4 ${activeFilter === 'reviewed' ? 'item-active' : ''}`}>
                                    <button
                                        onClick={() => handleFilterChange('reviewed')}
                                        className="bg-hover-primary-50 px-12 py-8 w-100 radius-8 text-secondary-light border-0"
                                    >
                                        <span className="d-flex align-items-center gap-10 justify-content-between w-100">
                                            <span className="d-flex align-items-center gap-10">
                                                <span className="icon text-xxl line-height-1 d-flex">
                                                    <Icon
                                                        icon="mdi:check-circle-outline"
                                                        className="icon line-height-1"
                                                    />
                                                </span>
                                                <span className="fw-semibold">Reviewed</span>
                                            </span>
                                            <span className="fw-medium">{getCountForStatus('REVIEWED')}</span>
                                        </span>
                                    </button>
                                </li>
                                <li className={`mb-4 ${activeFilter === 'resolved' ? 'item-active' : ''}`}>
                                    <button
                                        onClick={() => handleFilterChange('resolved')}
                                        className="bg-hover-primary-50 px-12 py-8 w-100 radius-8 text-secondary-light border-0"
                                    >
                                        <span className="d-flex align-items-center gap-10 justify-content-between w-100">
                                            <span className="d-flex align-items-center gap-10">
                                                <span className="icon text-xxl line-height-1 d-flex">
                                                    <Icon
                                                        icon="mdi:check-all"
                                                        className="icon line-height-1"
                                                    />
                                                </span>
                                                <span className="fw-semibold">Resolved</span>
                                            </span>
                                            <span className="fw-medium">{getCountForStatus('RESOLVED')}</span>
                                        </span>
                                    </button>
                                </li>
                                <li className={`mb-4 ${activeFilter === 'rejected' ? 'item-active' : ''}`}>
                                    <button
                                        onClick={() => handleFilterChange('rejected')}
                                        className="bg-hover-primary-50 px-12 py-8 w-100 radius-8 text-secondary-light border-0"
                                    >
                                        <span className="d-flex align-items-center gap-10 justify-content-between w-100">
                                            <span className="d-flex align-items-center gap-10">
                                                <span className="icon text-xxl line-height-1 d-flex">
                                                    <Icon
                                                        icon="ph:x-circle"
                                                        className="icon line-height-1"
                                                    />
                                                </span>
                                                <span className="fw-semibold">Rejected</span>
                                            </span>
                                            <span className="fw-medium">{getCountForStatus('REJECTED')}</span>
                                        </span>
                                    </button>
                                </li>
                                <li className={`mb-4 ${activeFilter === 'public' ? 'item-active' : ''}`}>
                                    <button
                                        onClick={() => handleFilterChange('public')}
                                        className="bg-hover-primary-50 px-12 py-8 w-100 radius-8 text-secondary-light border-0"
                                    >
                                        <span className="d-flex align-items-center gap-10 justify-content-between w-100">
                                            <span className="d-flex align-items-center gap-10">
                                                <span className="icon text-xxl line-height-1 d-flex">
                                                    <Icon
                                                        icon="mdi:earth"
                                                        className="icon line-height-1"
                                                    />
                                                </span>
                                                <span className="fw-semibold">Public</span>
                                            </span>
                                            <span className="fw-medium">-</span>
                                        </span>
                                    </button>
                                </li>
                            </ul>
                            <div className="mt-24">
                                <h6 className="text-lg fw-semibold text-primary-light mb-16">
                                    FEEDBACK TYPES
                                </h6>
                                <ul>
                                    <li className={`mb-4 ${activeFilter === 'general' ? 'item-active' : ''}`}>
                                        <button
                                            onClick={() => handleFilterChange('general')}
                                            className="bg-hover-primary-50 px-12 py-8 w-100 radius-8 text-secondary-light border-0"
                                        >
                                            <span className="d-flex align-items-center gap-10 justify-content-between w-100">
                                                <span className="d-flex align-items-center gap-10">
                                                    <span className="w-8-px h-8-px bg-primary-600 rounded-circle" />
                                                    <span className="fw-medium">General Feedback</span>
                                                </span>
                                                <span className="fw-medium">{getCountForType('GENERAL')}</span>
                                            </span>
                                        </button>
                                    </li>
                                    <li className={`mb-4 ${activeFilter === 'bug-reports' ? 'item-active' : ''}`}>
                                        <button
                                            onClick={() => handleFilterChange('bug-reports')}
                                            className="bg-hover-primary-50 px-12 py-8 w-100 radius-8 text-secondary-light border-0"
                                        >
                                            <span className="d-flex align-items-center gap-10 justify-content-between w-100">
                                                <span className="d-flex align-items-center gap-10">
                                                    <span className="w-8-px h-8-px bg-danger-600 rounded-circle" />
                                                    <span className="fw-medium">Bug Reports</span>
                                                </span>
                                                <span className="fw-medium">{getCountForType('BUG_REPORT')}</span>
                                            </span>
                                        </button>
                                    </li>
                                    <li className={`mb-4 ${activeFilter === 'feature-requests' ? 'item-active' : ''}`}>
                                        <button
                                            onClick={() => handleFilterChange('feature-requests')}
                                            className="bg-hover-primary-50 px-12 py-8 w-100 radius-8 text-secondary-light border-0"
                                        >
                                            <span className="d-flex align-items-center gap-10 justify-content-between w-100">
                                                <span className="d-flex align-items-center gap-10">
                                                    <span className="w-8-px h-8-px bg-success-600 rounded-circle" />
                                                    <span className="fw-medium">Feature Requests</span>
                                                </span>
                                                <span className="fw-medium">{getCountForType('FEATURE_REQUEST')}</span>
                                            </span>
                                        </button>
                                    </li>
                                    <li className={`mb-4 ${activeFilter === 'content-feedback' ? 'item-active' : ''}`}>
                                        <button
                                            onClick={() => handleFilterChange('content-feedback')}
                                            className="bg-hover-primary-50 px-12 py-8 w-100 radius-8 text-secondary-light border-0"
                                        >
                                            <span className="d-flex align-items-center gap-10 justify-content-between w-100">
                                                <span className="d-flex align-items-center gap-10">
                                                    <span className="w-8-px h-8-px bg-warning-600 rounded-circle" />
                                                    <span className="fw-medium">Content Feedback</span>
                                                </span>
                                                <span className="fw-medium">{getCountForType('CONTENT_FEEDBACK')}</span>
                                            </span>
                                        </button>
                                    </li>
                                </ul>
                            </div>
                            {feedbackStats && (
                                <div className="mt-24">
                                    <h6 className="text-lg fw-semibold text-primary-light mb-16">
                                        FEEDBACK STATS
                                    </h6>
                                    <div className="px-12 py-16 bg-neutral-100 rounded-3">
                                        <div className="mb-3">
                                            <span className="d-block text-sm text-secondary-light">Average Rating</span>
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="text-warning-600">
                                                    {Array(Math.round(feedbackStats.averageRating)).fill().map((_, i) => (
                                                        <Icon key={i} icon="mdi:star" className="icon" />
                                                    ))}
                                                </span>
                                                <span className="fw-semibold text-primary-light">{feedbackStats.averageRating.toFixed(1)}</span>
                                            </div>
                                        </div>
                                        <div>
                                            <span className="d-block text-sm text-secondary-light">Recent Feedback (30 days)</span>
                                            <span className="fw-semibold text-primary-light">{feedbackStats.recentCount}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="col-xxl-9">
                <div className="card h-100 p-0 feedback-card">
                    <div className="card-header border-bottom bg-base py-16 px-24">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-4">
                            <div className="d-flex align-items-center gap-3">
                                <div className="form-check style-check d-flex align-items-center">
                                    <input
                                        className="form-check-input radius-4 border input-form-dark"
                                        type="checkbox"
                                        checked={selectedItems.length === feedbackItems.length && feedbackItems.length > 0}
                                        onChange={handleSelectAll}
                                        id="selectAll"
                                    />
                                </div>
                                {selectedItems.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={handleDeleteSelected}
                                        className="delete-button text-danger text-xl d-flex"
                                    >
                                        <Icon
                                            icon="material-symbols:delete-outline"
                                            className="icon line-height-1"
                                        />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => fetchFeedback()}
                                    className="reload-button text-secondary-light text-xl d-flex"
                                >
                                    <Icon icon="tabler:reload" className="icon" />
                                </button>
                                <div className="dropdown">
                                    <button
                                        type="button"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                        className="d-flex"
                                    >
                                        <Icon
                                            icon="entypo:dots-three-vertical"
                                            className="icon text-secondary-light"
                                        />
                                    </button>
                                    <ul className="dropdown-menu dropdown-menu-lg p-12 border bg-base shadow">
                                        <li>
                                            <button
                                                type="button"
                                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 d-flex align-items-center gap-10"
                                                onClick={() => handleFilterChange('all')}
                                            >
                                                <Icon
                                                    icon="material-symbols:mark-email-read-outline"
                                                    className="icon text-lg line-height-1"
                                                />
                                                View All Feedback
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                type="button"
                                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 d-flex align-items-center gap-10"
                                                onClick={() => handleFilterChange('pending')}
                                            >
                                                <Icon
                                                    icon="mdi:clock-outline"
                                                    className="icon text-lg line-height-1"
                                                />
                                                View Pending Feedback
                                            </button>
                                        </li>
                                        <li>
                                            <p className="ms-40 mt-8 text-secondary-light mb-0">
                                                Select items to see more actions
                                            </p>
                                        </li>
                                    </ul>
                                </div>
                                <form className="navbar-search d-lg-block d-none">
                                    <input
                                        type="text"
                                        className="bg-base h-40-px w-auto"
                                        name="search"
                                        placeholder="Search feedback"
                                    />
                                    <Icon icon="ion:search-outline" className="icon" />
                                </form>
                            </div>
                            <div className="d-flex align-items-center gap-3">
                                <span className="text-secondary-light line-height-1">
                                    {feedbackItems.length} items
                                </span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center p-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : error ? (
                            <div className="alert alert-danger m-4" role="alert">
                                {error}
                            </div>
                        ) : feedbackItems.length === 0 ? (
                            <div className="text-center p-5">
                                <Icon icon="ph:chats" className="icon text-6xl text-secondary-light mb-3" />
                                <h5 className="text-lg fw-semibold text-primary-light">No feedback found</h5>
                                <p className="text-secondary-light">No feedback items match your current filter.</p>
                            </div>
                        ) : (
                            <ul className="overflow-x-auto">
                                {feedbackItems.map((item) => (
                                    <li key={item.id} className="feedback-item px-24 py-16 d-flex gap-4 align-items-center border-bottom cursor-pointer bg-hover-neutral-200 min-w-max-content">
                                        <div className="form-check style-check d-flex align-items-center">
                                            <input
                                                className="form-check-input radius-4 border border-neutral-400"
                                                type="checkbox"
                                                checked={selectedItems.includes(item.id)}
                                                onChange={() => handleSelectFeedback(item.id)}
                                            />
                                        </div>
                                        
                                        <div className="text-warning-600 d-flex">
                                            {Array(item.rating).fill().map((_, i) => (
                                                <Icon
                                                    key={i}
                                                    icon="ph:star-fill"
                                                    className="icon-fill line-height-1"
                                                />
                                            ))}
                                        </div>
                                        
                                        <div 
                                            className="text-primary-light fw-medium text-md text-line-1 w-190-px"
                                            onClick={() => handleViewFeedback(item.id)}
                                        >
                                            {item.user?.firstName} {item.user?.lastName}
                                        </div>
                                        
                                        <div 
                                            className="text-primary-light fw-medium mb-0 text-line-1 max-w-600-px"
                                            onClick={() => handleViewFeedback(item.id)}
                                        >
                                            <span className="fw-semibold">{item.title}</span> - {item.content.substring(0, 80)}{item.content.length > 80 ? '...' : ''}
                                        </div>
                                        
                                        <div className="d-flex align-items-center gap-2 ms-auto">
                                            <span className={`badge ${
                                                item.status === 'PENDING' ? 'bg-warning text-dark' :
                                                item.status === 'REVIEWED' ? 'bg-primary' :
                                                item.status === 'RESOLVED' ? 'bg-success' :
                                                'bg-danger'
                                            }`}>
                                                {item.status}
                                            </span>
                                            
                                            <span className={`badge ${
                                                item.type === 'GENERAL' ? 'bg-primary' :
                                                item.type === 'BUG_REPORT' ? 'bg-danger' :
                                                item.type === 'FEATURE_REQUEST' ? 'bg-success' :
                                                'bg-warning text-dark'
                                            }`}>
                                                {item.type.replace('_', ' ')}
                                            </span>
                                            
                                            {item.isPublic && (
                                                <span className="badge bg-info">
                                                    PUBLIC
                                                </span>
                                            )}
                                        </div>
                                        
                                        <div className="text-primary-light fw-medium min-w-max-content ms-2">
                                            {new Date(item.createdAt).toLocaleDateString()}
                                        </div>
                                        
                                        <div className="d-flex gap-2">
                                            <button
                                                onClick={() => handleViewFeedback(item.id)}
                                                className="btn btn-sm btn-outline-primary"
                                            >
                                                <Icon icon="mdi:eye" className="icon" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteFeedback(item.id)}
                                                className="btn btn-sm btn-outline-danger"
                                            >
                                                <Icon icon="mdi:delete" className="icon" />
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
            
            {/* Feedback Response Modal */}
            {selectedFeedback && (
                <div className={`modal fade ${showResponseModal ? 'show d-block' : ''}`} tabIndex="-1" role="dialog">
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Feedback Details</h5>
                                <button type="button" className="btn-close" onClick={() => setShowResponseModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row mb-4">
                                    <div className="col-md-6">
                                        <h6 className="text-primary-light fw-semibold">User</h6>
                                        <p className="mb-1">
                                            {selectedFeedback.user?.firstName} {selectedFeedback.user?.lastName}
                                        </p>
                                        <p className="text-secondary-light mb-0">{selectedFeedback.user?.email}</p>
                                    </div>
                                    <div className="col-md-6">
                                        <h6 className="text-primary-light fw-semibold">Feedback Info</h6>
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <span className={`badge ${
                                                selectedFeedback.status === 'PENDING' ? 'bg-warning text-dark' :
                                                selectedFeedback.status === 'REVIEWED' ? 'bg-primary' :
                                                selectedFeedback.status === 'RESOLVED' ? 'bg-success' :
                                                'bg-danger'
                                            }`}>
                                                {selectedFeedback.status}
                                            </span>
                                            
                                            <span className={`badge ${
                                                selectedFeedback.type === 'GENERAL' ? 'bg-primary' :
                                                selectedFeedback.type === 'BUG_REPORT' ? 'bg-danger' :
                                                selectedFeedback.type === 'FEATURE_REQUEST' ? 'bg-success' :
                                                'bg-warning text-dark'
                                            }`}>
                                                {selectedFeedback.type.replace('_', ' ')}
                                            </span>
                                            
                                            {selectedFeedback.isPublic && (
                                                <span className="badge bg-info">
                                                    PUBLIC
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-secondary-light mb-0">
                                            Submitted on {new Date(selectedFeedback.createdAt).toLocaleString()}
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="mb-4">
                                    <h6 className="text-primary-light fw-semibold">Rating</h6>
                                    <div className="text-warning-600 d-flex">
                                        {Array(selectedFeedback.rating).fill().map((_, i) => (
                                            <Icon
                                                key={i}
                                                icon="ph:star-fill"
                                                className="icon-fill line-height-1"
                                            />
                                        ))}
                                        {Array(5 - selectedFeedback.rating).fill().map((_, i) => (
                                            <Icon
                                                key={i}
                                                icon="ph:star"
                                                className="icon-outline line-height-1"
                                            />
                                        ))}
                                        <span className="ms-2 fw-semibold">{selectedFeedback.rating} / 5</span>
                                    </div>
                                </div>
                                
                                <div className="mb-4">
                                    <h6 className="text-primary-light fw-semibold">Title</h6>
                                    <p className="fw-semibold">{selectedFeedback.title}</p>
                                </div>
                                
                                <div className="mb-4">
                                    <h6 className="text-primary-light fw-semibold">Content</h6>
                                    <p>{selectedFeedback.content}</p>
                                </div>
                                
                                <div className="mb-4">
                                    <h6 className="text-primary-light fw-semibold">Admin Response</h6>
                                    <textarea
                                        className="form-control"
                                        rows="4"
                                        value={adminResponse}
                                        onChange={(e) => setAdminResponse(e.target.value)}
                                        placeholder="Enter your response to this feedback..."
                                    ></textarea>
                                </div>
                                
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <h6 className="text-primary-light fw-semibold">Status</h6>
                                        <select
                                            className="form-select"
                                            value={responseStatus}
                                            onChange={(e) => setResponseStatus(e.target.value)}
                                        >
                                            <option value="PENDING">Pending</option>
                                            <option value="REVIEWED">Reviewed</option>
                                            <option value="RESOLVED">Resolved</option>
                                            <option value="REJECTED">Rejected</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <h6 className="text-primary-light fw-semibold">Visibility</h6>
                                        <div className="form-check form-switch mt-2">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="makePublic"
                                                checked={isPublic}
                                                onChange={(e) => setIsPublic(e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor="makePublic">
                                                Make public (display as testimonial)
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => setShowResponseModal(false)}
                                >
                                    Close
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary"
                                    onClick={handleSubmitResponse}
                                >
                                    Submit Response
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Modal backdrop */}
            {showResponseModal && (
                <div className="modal-backdrop fade show"></div>
            )}
        </div>
    )
}

export default FeedbackManagement